'use client';

import { FC, ReactNode } from 'react';

import { AuthStoreProvider, useCreateAuthStore } from '@/stores/authStore';
import { CarComparisonStoreProvider, useCreateCarComparisonStore } from '@/stores/carComparisonStore';
import { CarStatusStoreProvider, useCreateCarStatusStore } from '@/stores/carStatusStore';
import { CartStoreProvider, useCreateCartStore } from '@/stores/cartStore';
import { DocumentStoreProvider, useCreateDocumentStore } from '@/stores/documentStore';
import { LiveChatStoreProvider, useCreateLiveChatStore } from '@/stores/liveChatStore';
import { AuthManager } from '@/stores/managers/AuthManager';
import { CarStatusManager } from '@/stores/managers/CarStatusManager';
import { CartManager } from '@/stores/managers/CartManager';
import { SavedSearchManager } from '@/stores/managers/SavedSearchManager';
import { WatchListManager } from '@/stores/managers/WatchListManager';
import { ModalStore } from '@/stores/modalStore';
import { RecentFilterSearchStoreProvider, useCreateRecentFilterSearchStore } from '@/stores/recentFilterSearchStore';
import { SavedSearchStoreProvider, useCreateSavedSearchStore } from '@/stores/savedSearchStore';
import { WatchlistStoreProvider, useCreateWatchlistStore } from '@/stores/watchListStore';

interface Props {
  children: ReactNode;
}

/**
 * Zustand store providers and manager, focussed around Authentication and Snackbar provider
 */
export const StoreProviders: FC<Props> = ({ children }) => {
  const createAuthStore = useCreateAuthStore({});
  const createCartStore = useCreateCartStore({});
  const createSavedSearchStore = useCreateSavedSearchStore({});
  const createWatchlistStore = useCreateWatchlistStore({});
  const createLiveChatStore = useCreateLiveChatStore({});
  const createCarStatusStore = useCreateCarStatusStore({});
  const createRecentFilterSearchStore = useCreateRecentFilterSearchStore({});
  const createCarComparisonStore = useCreateCarComparisonStore({});
  const createDocumentStore = useCreateDocumentStore({});

  return (
    <CarStatusStoreProvider createStore={createCarStatusStore}>
      <AuthStoreProvider createStore={createAuthStore}>
        <SavedSearchStoreProvider createStore={createSavedSearchStore}>
          <CartStoreProvider createStore={createCartStore}>
            <DocumentStoreProvider createStore={createDocumentStore}>
              <WatchlistStoreProvider createStore={createWatchlistStore}>
                <LiveChatStoreProvider createStore={createLiveChatStore}>
                  <RecentFilterSearchStoreProvider createStore={createRecentFilterSearchStore}>
                    <CarComparisonStoreProvider createStore={createCarComparisonStore}>
                      <ModalStore>
                        {/* Store managers */}
                        <AuthManager />
                        <CarStatusManager />
                        <SavedSearchManager />
                        <CartManager />
                        <WatchListManager />
                        {children}
                      </ModalStore>
                    </CarComparisonStoreProvider>
                  </RecentFilterSearchStoreProvider>
                </LiveChatStoreProvider>
              </WatchlistStoreProvider>
            </DocumentStoreProvider>
          </CartStoreProvider>
        </SavedSearchStoreProvider>
      </AuthStoreProvider>
    </CarStatusStoreProvider>
  );
};
